import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Award } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { SectionHeading } from "@/components/SectionHeading";
import { ProjectCard } from "@/components/ProjectCard";
import { CTASection } from "@/components/CTASection";
import { Testimonials } from "@/components/Testimonials";
import { Seo } from "@/components/Seo";
import { SERVICES, BUSINESS } from "@/data/site";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const STATS = [
  { value: "12+", label: "Years of Experience" },
  { value: "100+", label: "Projects Delivered" },
  { value: "100%", label: "On-Time Commitment" },
  { value: "5★", label: "Client Satisfaction" },
];

const VALUES = [
  { icon: Award, title: "Quality Craftsmanship", desc: "Every joint, pour and finish executed to the highest standard." },
  { icon: ShieldCheck, title: "Dependable Service", desc: "Trusted partners who show up and deliver on every promise." },
  { icon: Clock, title: "On Time & On Budget", desc: "Disciplined project management with no surprises." },
];

export default function Home() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setProjects(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <Seo />
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Modern construction in Belize" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 w-full pt-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-bold text-gold mb-6">Placencia Village · Belize</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Building Belize's <span className="text-gold-gradient">Coastline</span>, One Landmark at a Time.
            </h1>
            <p className="mt-7 text-lg text-white/80 max-w-xl leading-relaxed">
              Residential & commercial construction, waterfront structures, docks and land development — crafted with precision for over {BUSINESS.years} years.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 font-bold tracking-wide hover:bg-gold-light transition-colors group">
                Get a Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/projects" data-testid="hero-projects-btn" className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 font-semibold hover:border-gold hover:text-gold transition-colors">
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-navy-light">
        <div className="max-w-7xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="py-10 px-6 text-center">
              <div className="font-display text-4xl sm:text-5xl text-gold">{s.value}</div>
              <div className="mt-2 text-white/60 text-sm tracking-wide">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-gold" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-5 py-4 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-navy text-sm font-bold tracking-wide">
          <span className="flex items-center gap-2"><ShieldCheck size={18} /> Licensed &amp; Insured</span>
          <span className="hidden sm:inline text-navy/40">•</span>
          <span className="flex items-center gap-2"><Award size={18} /> {BUSINESS.years} Years Experience</span>
          <span className="hidden sm:inline text-navy/40">•</span>
          <span className="flex items-center gap-2"><Clock size={18} /> Free, No-Obligation Quotes</span>
        </div>
      </section>

      {/* About intro */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading
              overline="Who We Are"
              title="A Belizean builder committed to permanence."
              subtitle="Crawford Construction & Development Ltd. specializes in residential and commercial construction across Belize. From concrete and wooden homes to waterfront structures, docks, piers, seawalls and land development, we turn our clients' vision into reality — with precision in every project."
            />
            <div className="mt-8 space-y-3">
              {["Quality craftsmanship on every build", "Dependable, transparent service", "Delivered on time and within budget"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-navy font-medium">
                  <CheckCircle2 size={20} className="text-gold shrink-0" /> {t}
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 mt-9 text-navy font-bold border-b-2 border-gold pb-1 hover:gap-3 transition-all">
              Learn More About Us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1625390711106-3728815ebcd9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Concrete home" className="w-full h-64 object-cover mt-8" />
            <img src="https://images.unsplash.com/photo-1702743599501-a821d0b38b66?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Waterfront dock" className="w-full h-64 object-cover" />
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading overline="What We Do" title="Comprehensive construction services." subtitle="From private homes to commercial developments and coastal structures — one dependable partner for every phase." center />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Hammer;
              const span = i === 0 ? "md:col-span-6" : i === 1 ? "md:col-span-6" : "md:col-span-4";
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 3) * 0.08 }}
                  className={`${span} bg-white border border-gray-100 p-8 hover:border-gold hover:shadow-lg transition-all duration-300 group`}
                >
                  <div className="h-12 w-12 flex items-center justify-center bg-navy text-gold group-hover:bg-gold group-hover:text-navy transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl text-navy mt-6">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-14">
            <SectionHeading overline="Our Work" title="Featured projects." />
            <Link to="/projects" className="inline-flex items-center gap-2 text-navy font-bold hover:text-gold transition-colors shrink-0">
              View All Projects <ArrowRight size={16} />
            </Link>
          </div>
          {projects.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((p, i) => (
                <Link key={p.id} to="/projects"><ProjectCard project={p} index={i} /></Link>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">Projects coming soon.</p>
          )}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading overline="Why Choose Us" title="Precision in every project." light center />
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="border border-white/10 p-8 hover:border-gold/50 transition-colors">
                <v.icon size={32} className="text-gold" />
                <h3 className="font-display text-2xl text-white mt-5">{v.title}</h3>
                <p className="mt-3 text-white/60 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <CTASection />
    </PublicLayout>
  );
}
